export type JlifeErrorCode =
  | 'access_denied'
  | 'invalid_state'
  | 'link_taken'
  | 'account_deleted'
  | 'login_required'
  | 'unavailable'
  | 'unknown';

export interface ParsedJlifeError {
  code: JlifeErrorCode;
  description?: string;
}

// Shown when JLife / the API gave no `error_description` (5xx, network failure, empty body).
export const JLIFE_FALLBACK_ERROR_KEY = 'auth.jlifeCallback.errors.generic';

interface JlifeRequestError {
  response?: {
    status?: number;
    responseText?: string;
  };
}

const KNOWN_ERROR_CODES: JlifeErrorCode[] = [
  'access_denied',
  'invalid_state',
  'link_taken',
  'account_deleted',
  'login_required',
];

// The API only accepts (and returns) a path inside Jlive; anything else is treated as "no destination".
// Backslashes and control characters are rejected because browsers normalise `/\host` to `//host`; the origin check
// is the final guard.
export const toInternalPath = (value: unknown): string | null => {
  if (typeof value !== 'string' || !value.startsWith('/') || value.startsWith('//')) return null;
  if (Array.from(value).some((char) => char === '\\' || char.charCodeAt(0) <= 0x1f)) return null;

  try {
    const base = typeof window !== 'undefined' ? window.location.origin : 'https://placeholder.invalid';
    const url = new URL(value, base);
    return url.origin === base ? `${url.pathname}${url.search}${url.hash}` : null;
  } catch (e) {
    return null;
  }
};

// The Median native shell keeps the old login modal (JLife's page is not used there).
export const isMedianApp = () => typeof navigator !== 'undefined' && navigator.userAgent.includes('median');

// Where to land after signing in: `redirect_to` (set by withAuth) wins, otherwise the current page without its query.
export const getLoginDestination = (asPath: string, redirectTo?: unknown): string | undefined => {
  const redirectPath = toInternalPath(redirectTo);
  if (redirectPath) return redirectPath;

  const currentPath = toInternalPath(asPath?.split(/[?#]/)[0]);
  return currentPath && currentPath !== '/' ? currentPath : undefined;
};

export const parseJlifeError = (error: unknown): ParsedJlifeError => {
  const { response } = (error || {}) as JlifeRequestError;

  let body: { error?: string; error_description?: string } = {};
  try {
    body = JSON.parse(response?.responseText) || {};
  } catch (e) {}

  const description = body.error_description;
  const code = KNOWN_ERROR_CODES.find((knownCode) => knownCode === body.error);
  if (code) return { code, description };
  if (response?.status >= 500) return { code: 'unavailable', description };

  return { code: 'unknown', description };
};
